import { camelCase, snakeCase } from 'lodash';
import type { SortDirection } from '@mui/material/TableCell';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';

export interface ColumnData {
  id: string;
  numeric?: boolean;
  disablePadding?: boolean;
  cellPadding?: 'dense' | 'default' | 'none';
  label?: React.ReactNode;
  defaultOrderDirection?: 'asc' | 'desc' | null | undefined;
  unsortable?: boolean;
}

interface Props {
  orderBy: string | undefined;
  onChangeOrderBy: (order: string) => void;
  columns: ColumnData[];
  selected?: boolean | undefined;
}

export const SortableTableHead = ({
  orderBy,
  onChangeOrderBy,
  columns,
  selected,
}: Props) => {
  const { id: orderId, direction: orderDirection } = getOrderInfo(orderBy);
  return (
    <TableHead>
      <TableRow selected={selected}>
        {columns.map((column) => {
          return (
            <TableCell
              key={column.id}
              align={column.numeric ? 'right' : 'inherit'}
              size={column.cellPadding === 'dense' ? 'small' : 'medium'}
              padding={column.disablePadding ? 'none' : 'normal'}
              sortDirection={orderId === column.id ? orderDirection : false}
            >
              {column.label && !column.unsortable ? (
                <Tooltip
                  title="Sort"
                  placement={column.numeric ? 'bottom-end' : 'bottom-start'}
                  enterDelay={300}
                >
                  <TableSortLabel
                    active={orderId === column.id}
                    direction={orderDirection || undefined}
                    onClick={() => {
                      let newOrderDirection =
                        column.defaultOrderDirection || 'desc';

                      if (orderId === column.id) {
                        newOrderDirection =
                          orderDirection === 'asc' ? 'desc' : 'asc';
                      }

                      let snaked = snakeCase(column.id);
                      if (newOrderDirection === 'desc') {
                        snaked += '_desc';
                      }

                      onChangeOrderBy(snaked.toUpperCase());
                    }}
                  >
                    {column.label}
                  </TableSortLabel>
                </Tooltip>
              ) : (
                column.label || null
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

export function getOrderInfo(orderBy: string | undefined) {
  let camelCased = camelCase(orderBy?.toLowerCase());
  let order = 'asc';
  if (camelCased.endsWith('Desc')) {
    camelCased = camelCased.replace(/Desc$/, '');
    order = 'desc';
  }

  return {
    id: camelCased,
    direction: order as SortDirection,
  };
}
